import React from 'react';
import { Municipality, getFeesForYear, getYearOverYearChange, filterFees, DEFAULT_ENABLED_CATEGORIES } from '@/lib/data';

interface TopplisteWidgetProps {
  municipalities: Municipality[];
  year: number;
  limit: number;
  sort: 'asc' | 'desc';
  enabledCategories?: string[];
  dark?: boolean;
}

const getChangeClass = (change: number, dark?: boolean) => {
  if (change > 0) return dark ? 'text-amber-400' : 'text-amber-600';
  if (change < 0) return dark ? 'text-slate-400' : 'text-slate-600';
  return dark ? 'text-slate-500' : 'text-warmGray';
};

export const TopplisteWidget: React.FC<TopplisteWidgetProps> = ({
  municipalities,
  year,
  limit,
  sort,
  enabledCategories = DEFAULT_ENABLED_CATEGORIES,
  dark,
}) => {
  const data = municipalities
    .map(m => ({
      m,
      total: filterFees(getFeesForYear(m, year), enabledCategories).reduce((s, f) => s + f.amount, 0),
      change: getYearOverYearChange(m, year, enabledCategories),
    }))
    .filter(x => x.total > 0)
    .sort((a, b) => (sort === 'desc' ? b.total - a.total : a.total - b.total))
    .slice(0, limit);

  return (
    <div className="space-y-1">
      {data.map(({ m, total, change }, i) => (
        <div
          key={m.id}
          className={`flex items-center gap-3 py-2 px-3 rounded-lg ${dark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-50'}`}
        >
          <span
            className={`w-5 text-xs font-medium tabular-nums shrink-0 ${
              dark ? 'text-slate-500' : 'text-warmGray'
            }`}
          >
            {i + 1}
          </span>
          <div className="flex-1 min-w-0">
            <p className={`text-sm truncate ${dark ? 'text-slate-200' : 'text-deepCharcoal'}`}>{m.name}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {change !== null && (
              <span className={`text-xs tabular-nums ${getChangeClass(change, dark)}`}>
                {change > 0 ? '+' : ''}{change.toFixed(1)}%
              </span>
            )}
            <p className={`text-sm tabular-nums ${dark ? 'text-slate-400' : 'text-nordicBlue'}`}>
              {total.toLocaleString('nb-NO')} kr
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};
